import { Injectable, inject } from '@angular/core';
import { Observable, map, catchError, of, switchMap, forkJoin } from 'rxjs';
import { ServicioContenidoModyo } from '../modyo/modyo-contenido.service';
import type {
  MensajeErrorServicio,
  MensajeErrorServicioRawFields,
  OpcionesBusquedaMensajeError,
  RespuestaErrorServicio,
  ModyoEntryResponse,
  CampoReferenciaModyo,
  CamposTituloModyo,
  CamposTextoEnriquecidoModyo,
  EntradaReferenciadaModyo,
  PesoTitulo,
} from '@core/interfaces/mensaje-error.type';
import { PRIMERA_ENTRA_INDEX } from '@core/interfaces/mensaje-error.type';

interface MetadatosTitulo {
  texto: string;
  peso: PesoTitulo;
  pesoFuente: string;
}

interface DatosParaConstruirMensaje {
  entrada: ModyoEntryResponse<MensajeErrorServicioRawFields>;
  tituloResuelto: string;
  descripcionResuelta: string;
  pesoTitulo?: PesoTitulo;
  pesoFuenteTitulo?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MensajeErrorServicioService {
  private readonly servicioContenidoModyo = inject(ServicioContenidoModyo);
  private readonly CONTENT_TYPE_UID = 'mensaje-servicio';
  private readonly SIN_ELEMENTOS = 0;
  private readonly PRIMER_ELEMENTO = 0;
  private readonly PESO_TITULO_POR_DEFECTO: PesoTitulo = 'h4';
  private readonly PESO_FUENTE_TITULO_POR_DEFECTO = '700';
  private readonly PESOS_TITULO_VALIDOS: PesoTitulo[] = ['h1', 'h2', 'h3', 'h4'];

  public obtenerMensajeError(opciones: OpcionesBusquedaMensajeError): Observable<MensajeErrorServicio | null> {
    const { codigo, codigoHttp, tipoServicio } = opciones;

    const filtro = this.servicioContenidoModyo.crearFiltro(this.CONTENT_TYPE_UID);
    filtro['Equals']('fields.codigo', codigo);

    if (codigoHttp) {
      filtro['Equals']('fields.codigo_http', String(codigoHttp));
    }

    if (tipoServicio) {
      filtro['Equals']('fields.tipo_servicio', tipoServicio);
    }

    return this.servicioContenidoModyo
      .obtenerEntradas<MensajeErrorServicioRawFields>(this.CONTENT_TYPE_UID, { filter: filtro })
      .pipe(
        switchMap((respuesta) => {
          if (respuesta.entries && respuesta.entries.length > PRIMERA_ENTRA_INDEX) {
            const entrada = respuesta.entries[
              PRIMERA_ENTRA_INDEX
            ] as unknown as ModyoEntryResponse<MensajeErrorServicioRawFields>;
            return this.resolverReferenciasYNormalizar(entrada);
          }
          return of(null);
        }),
        catchError(() => of(null)),
      );
  }

  public extraerOpcionesDeRespuesta(respuesta: RespuestaErrorServicio): OpcionesBusquedaMensajeError {
    const codigoRaw = respuesta.additionalStatus?.code ?? respuesta.status?.code ?? 'desconocido';
    const codigoNormalizado = codigoRaw === '' ? 'desconocido' : codigoRaw;
    const codigo = String(codigoNormalizado);
    const codigoHttp = respuesta.status?.code;

    return {
      codigo,
      codigoHttp,
    };
  }

  public detectarTipoServicio(respuesta: unknown): string {
    if (!respuesta || typeof respuesta !== 'object') {
      return 'Todos';
    }

    const resp = respuesta as Record<string, unknown>;

    const documentoIdPropia = resp['documentID'];
    const documentoIdEnCustomer = resp['customer'] && Object.hasOwn(resp['customer'] as object, 'documentID');
    const contieneDocumentoId = Boolean(documentoIdPropia) || Boolean(documentoIdEnCustomer);

    if (contieneDocumentoId) {
      return 'Customer.InqValidateDocument';
    }

    if (resp['restrictiveLists'] || resp['documentValidation']) {
      return 'Customer_InqAllRestrictiveLists';
    }

    if (resp['simulationResult']) {
      return 'Account_InqSavingAccountSimulation';
    }

    return 'Todos';
  }

  public crearMensajeGenerico(opciones: OpcionesBusquedaMensajeError): MensajeErrorServicio {
    return {
      codigo: opciones.codigo,
      codigoHttp: String(opciones.codigoHttp ?? '500'),
      tituloMensaje: 'Error en el servicio',
      pesoTitulo: this.PESO_TITULO_POR_DEFECTO,
      pesoFuenteTitulo: this.PESO_FUENTE_TITULO_POR_DEFECTO,
      descripcionMensaje:
        'Ocurrió un error al procesar tu solicitud. Por favor intenta nuevamente o contacta a soporte.',
      tipoServicio: opciones.tipoServicio ?? 'Todos',
      mensajeTecnico: 'Error genérico del sistema',
      nivelSeveridad: 'error',
      detieneProceso: false,
      icono: 'error',
    };
  }

  public obtenerMensajeDesdeRespuesta(respuesta: unknown): Observable<MensajeErrorServicio> {
    if (!respuesta || typeof respuesta !== 'object') {
      return of(this.crearMensajeGenerico({ codigo: 'desconocido' }));
    }

    const resp = respuesta as RespuestaErrorServicio;
    const opciones = this.extraerOpcionesDeRespuesta(resp);
    const tipoServicio = this.detectarTipoServicio(respuesta);

    return this.obtenerMensajeError({ ...opciones, tipoServicio }).pipe(
      map((mensaje) => mensaje ?? this.crearMensajeGenerico({ ...opciones, tipoServicio })),
    );
  }

  private resolverReferenciasYNormalizar(
    entrada: ModyoEntryResponse<MensajeErrorServicioRawFields>,
  ): Observable<MensajeErrorServicio> {
    const campos = entrada.fields;
    const referenciaTitulo = this.extraerReferencia(campos.titulo_mensaje);
    const referenciaDescripcion = this.extraerReferencia(campos.descripcion_mensaje);

    const sinReferenciasParaResolver = !referenciaTitulo && !referenciaDescripcion;
    if (sinReferenciasParaResolver) {
      return of(this.construirMensajeNormalizado({ entrada, tituloResuelto: '', descripcionResuelta: '' }));
    }

    const consultas: Record<string, Observable<string | MetadatosTitulo>> = {};

    if (referenciaTitulo) {
      consultas['titulo'] = this.obtenerMetadatosTituloPorReferencia(
        referenciaTitulo.content_type,
        referenciaTitulo.uuid,
      );
    }

    if (referenciaDescripcion) {
      consultas['descripcion'] = this.obtenerTextoPorReferencia(
        referenciaDescripcion.content_type,
        referenciaDescripcion.uuid,
      );
    }

    return forkJoin(consultas).pipe(
      map((resultados) => {
        const metadatosTitulo = resultados['titulo'] as MetadatosTitulo | undefined;
        const descripcion = (resultados['descripcion'] as string) ?? '';

        return this.construirMensajeNormalizado({
          entrada,
          tituloResuelto: metadatosTitulo?.texto ?? '',
          descripcionResuelta: descripcion,
          pesoTitulo: metadatosTitulo?.peso ?? this.PESO_TITULO_POR_DEFECTO,
          pesoFuenteTitulo: metadatosTitulo?.pesoFuente ?? this.PESO_FUENTE_TITULO_POR_DEFECTO,
        });
      }),
      catchError(() => of(this.construirMensajeNormalizado({ entrada, tituloResuelto: '', descripcionResuelta: '' }))),
    );
  }

  private obtenerMetadatosTituloPorReferencia(tipoContenido: string, uuid: string): Observable<MetadatosTitulo> {
    return this.servicioContenidoModyo
      .obtenerEntradaPorUuid<
        EntradaReferenciadaModyo<CamposTituloModyo | CamposTextoEnriquecidoModyo>
      >(tipoContenido, uuid)
      .pipe(
        map((entrada) => {
          const campos = entrada?.fields;
          const esTipoTitulo = campos && 'peso' in campos;

          if (esTipoTitulo) {
            return {
              texto: campos.texto ?? '',
              peso: this.normalizarPesoTitulo(campos.peso),
              pesoFuente: campos['font-weight'] || this.PESO_FUENTE_TITULO_POR_DEFECTO,
            };
          }

          return {
            texto: campos?.texto ?? '',
            peso: this.PESO_TITULO_POR_DEFECTO,
            pesoFuente: this.PESO_FUENTE_TITULO_POR_DEFECTO,
          };
        }),
        catchError(() =>
          of({
            texto: '',
            peso: this.PESO_TITULO_POR_DEFECTO,
            pesoFuente: this.PESO_FUENTE_TITULO_POR_DEFECTO,
          }),
        ),
      );
  }

  private obtenerTextoPorReferencia(tipoContenido: string, uuid: string): Observable<string> {
    return this.servicioContenidoModyo
      .obtenerEntradaPorUuid<
        EntradaReferenciadaModyo<CamposTituloModyo | CamposTextoEnriquecidoModyo>
      >(tipoContenido, uuid)
      .pipe(
        map((entrada) => entrada?.fields?.texto ?? ''),
        catchError(() => of('')),
      );
  }

  private extraerReferencia(
    campo: CampoReferenciaModyo | Record<string, never> | undefined,
  ): CampoReferenciaModyo | null {
    if (!campo || typeof campo !== 'object') {
      return null;
    }

    const esReferencia = 'uuid' in campo && 'content_type' in campo;
    return esReferencia ? (campo as CampoReferenciaModyo) : null;
  }

  private construirMensajeNormalizado(datos: DatosParaConstruirMensaje): MensajeErrorServicio {
    const campos = datos.entrada.fields;
    const pesoTitulo = datos.pesoTitulo ?? this.PESO_TITULO_POR_DEFECTO;
    const pesoFuenteTitulo = datos.pesoFuenteTitulo ?? this.PESO_FUENTE_TITULO_POR_DEFECTO;

    return {
      codigo: this.extraerValorDirecto(campos.codigo),
      codigoHttp: this.extraerValorDirecto(campos.codigo_http),
      tituloMensaje: datos.tituloResuelto,
      pesoTitulo,
      pesoFuenteTitulo,
      descripcionMensaje: datos.descripcionResuelta,
      tipoServicio: this.extraerPrimerElemento(campos.tipo_servicio),
      mensajeTecnico: this.extraerValorDirecto(campos.mensaje_tecnico),
      nivelSeveridad: this.extraerPrimerElemento(campos.nivel_severidad) as
        | 'exito'
        | 'advertencia'
        | 'error'
        | 'error_critico',
      detieneProceso: Boolean(campos.detiene_proceso),
      icono: this.extraerValorDirecto(campos.icono) as 'success' | 'warning' | 'error' | 'info',
      meta: datos.entrada.meta,
    };
  }

  private extraerValorDirecto(campo: string | number | Record<string, never> | undefined): string {
    if (campo === null || campo === undefined) {
      return '';
    }

    if (typeof campo === 'string' || typeof campo === 'number') {
      return String(campo);
    }

    const esObjetoVacio = typeof campo === 'object' && Object.keys(campo).length === this.SIN_ELEMENTOS;
    return esObjetoVacio ? '' : String(campo);
  }

  private extraerPrimerElemento(campo: string[] | undefined): string {
    if (!Array.isArray(campo) || campo.length === this.SIN_ELEMENTOS) {
      return '';
    }
    return String(campo[this.PRIMER_ELEMENTO]);
  }

  private normalizarPesoTitulo(peso: string | undefined): PesoTitulo {
    const pesoNormalizado = peso?.toLowerCase() as PesoTitulo;
    const esPesoValido = this.PESOS_TITULO_VALIDOS.includes(pesoNormalizado);

    return esPesoValido ? pesoNormalizado : this.PESO_TITULO_POR_DEFECTO;
  }
}
